import React, { useEffect, useState } from 'react';
import { isTrackingActive } from '../native/backgroundTracker';
import { TrackingSessionBanner } from '../components/TrackingSessionBanner';
import { ShareLocationPrompt } from '../components/ShareLocationPrompt';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Package, MapPin, CheckCircle, Clock, AlertCircle, Truck, ArrowRight,
  Calendar, Weight, Route as RouteIcon, MessageSquare, FileBadge2, Users,
  Fuel, Scale, Wrench, Loader2,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import LoadChatDrawer from '../components/LoadChatDrawer';
import { track } from '../lib/telemetry';
import LoadDocumentsModal from '../components/LoadDocumentsModal';
import { MissingDocsBadge } from '../components/MissingDocsChecklist';
import PreTripModal from '../components/PreTripModal';
import PostTripModal from '../components/PostTripModal';
import StopProgressCard from '../components/StopProgressCard';
import LoadStatusStepper from '../components/LoadStatusStepper';
import { DRIVER_IN_PROGRESS, statusLabel } from '../lib/loadStatus';
import { LoadStatusPill } from '../components/LoadStatusPill';
import OfflineBanner from '../components/OfflineBanner';
import DriverRequestModal from '../components/DriverRequestModal';
import DriverReceiptCard from '../components/DriverReceiptCard';
import LoadStopsMap from '../components/LoadStopsMap';
import ContractExpiryPanel from '../components/ContractExpiryPanel';
import {
  cacheLoads, getCachedLoads, patchCachedLoad, enqueueOperation, queueCount,
} from '../db/offlineStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriverDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [driverStatus, setDriverStatus] = useState(null);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [chatLoad, setChatLoad] = useState(null);
  const [docsLoad, setDocsLoad] = useState(null);
  const [preTripLoad, setPreTripLoad] = useState(null);
  const [postTripLoad, setPostTripLoad] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestLoadId, setRequestLoadId] = useState(null);
  const [stopPrefill, setStopPrefill] = useState(null);
  const [stopBusy, setStopBusy] = useState(null);
  const [receiptRefresh, setReceiptRefresh] = useState(0);

  const getCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null), { timeout: 5000 });
  });

  const labels = { fuel: 'Fuel', scale: 'Scale / weigh', repair: 'Repair / maintenance' };

  const stopWithPhoto = async (loadId, stopType, file) => {
    if (!loadId || !file) return;
    setStopBusy(stopType);
    const note = `${labels[stopType] || stopType} stop`;
    try {
      const coords = await getCoords();
      await axios.post(`${API}/loads/${loadId}/stop`, { stop_type: stopType, note, ...(coords || {}) }, { withCredentials: true });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('stop_type', stopType);
      fd.append('load_id', loadId);
      fd.append('note', note);
      await axios.post(`${API}/tax/driver/receipt`, fd, { withCredentials: true });
      setStopPrefill({ stopType, note, ts: Date.now(), done: true });
      setReceiptRefresh((n) => n + 1);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not save the stop + receipt.');
    } finally { setStopBusy(null); }
  };

  const logStop = async (loadId, stopType) => {
    if (!loadId) return;
    setStopBusy(stopType);
    const post = (coords) => axios.post(`${API}/loads/${loadId}/stop`,
      { stop_type: stopType, note: `${labels[stopType] || stopType} stop`, ...(coords || {}) },
      { withCredentials: true });
    const finish = () => { setStopPrefill({ stopType, note: `${labels[stopType] || stopType} stop`, ts: Date.now() }); setStopBusy(null); };
    const fail = (e) => { alert(e?.response?.data?.detail || 'Could not log stop.'); setStopBusy(null); };
    const coords = await getCoords();
    try { await post(coords); finish(); } catch (e) { fail(e); }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Fleet Sync Health heartbeat — drivers ping the backend every 60s while
  // the tab is open. Backend uses this to compute the admin dashboard's
  // "how many drivers are online / carrying an offline backlog" widget,
  // and (when GPS is available) power the Driver Heartbeat Map. GPS is
  // best-effort: a short 4s timeout keeps the heartbeat snappy, and we
  // silently ship a position-less heartbeat if permission is denied or
  // the device has no fix. Never blocks the driver's real work.
  useEffect(() => {
    let mounted = true;

    const getPosition = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(null);
          return;
        }
        try {
          navigator.geolocation.getCurrentPosition(
            (p) => resolve({
              lat: p.coords.latitude,
              lng: p.coords.longitude,
              accuracy_m: p.coords.accuracy,
            }),
            () => resolve(null),
            { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 },
          );
        } catch {
          resolve(null);
        }
      });

    const beat = async () => {
      try {
        const pending = await queueCount();
        // Location is only attached while the driver has an active tracking session running.
        const gps = isTrackingActive() ? await getPosition() : null;
        const body = {
          pending_ops_count: pending,
          is_online: navigator.onLine !== false,
        };
        if (gps) {
          body.gps_lat = gps.lat;
          body.gps_lng = gps.lng;
          body.gps_accuracy_m = gps.accuracy_m;
        }
        await axios.post(`${API}/driver-heartbeat`, body, { withCredentials: true });
      } catch { /* offline / auth expired — retry on next tick */ }
      if (!mounted) return;
    };
    beat();
    const interval = setInterval(beat, 60_000);
    const onFocus = () => beat();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const fetchAll = async () => {
    // Online path: fetch fresh, then mirror to IndexedDB for offline reads.
    try {
      const meRes = await axios.get(`${API}/drivers/me`, { withCredentials: true });
      setDriverStatus(meRes.data.driver.status);
      if (meRes.data.driver.status === 'active') {
        const loadsRes = await axios.get(`${API}/loads`, { withCredentials: true });
        setLoads(loadsRes.data);
        try { await cacheLoads(loadsRes.data); } catch { /* IDB unavailable — non-fatal */ }
      }
    } catch (e) {
      // Offline / network error: fall back to the last cached snapshot so the
      // driver can still see today's load, addresses, contacts, and stops.
      try {
        const cached = await getCachedLoads();
        if (cached && cached.length > 0) {
          setDriverStatus('active');
          setLoads(cached);
          return;
        }
      } catch { /* ignore */ }
      setDriverStatus('unknown');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (loadId, newStatus) => {
    setUpdating(loadId);
    // Generate an operation_id per attempt. Backend uses it to de-duplicate
    // replays, so the same status transition retried offline → online → server
    // is only applied once.
    const opId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    if (!navigator.onLine) {
      // Offline: enqueue the operation, optimistically patch the local cache,
      // and update UI immediately. Sync engine will drain on reconnect.
      try {
        await enqueueOperation({
          opId, kind: 'load_status', loadId, payload: { status: newStatus },
        });
        await patchCachedLoad(loadId, { status: newStatus, _pending_sync: true });
        setLoads((prev) => prev.map((l) => (l.id === loadId ? { ...l, status: newStatus, _pending_sync: true } : l)));
      } catch (err) {
        alert('Could not save offline — please try again when back online.');
      } finally {
        setUpdating(null);
      }
      return;
    }

    try {
      await axios.post(
        `${API}/loads/${loadId}/status`,
        { status: newStatus, operation_id: opId },
        { withCredentials: true }
      );
      await fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  if (driverStatus && driverStatus !== 'active') {
    const statusLabels = {
      registered: 'Application Incomplete',
      pending_approval: 'Pending Approval',
      invited: 'Onboarding Not Started',
    };
    return (
      <DashboardLayout>
        <div className="p-6 max-w-5xl mx-auto">
          <div data-testid="driver-status-banner" className="bg-orange-50 border border-orange-200 rounded-md p-6 mb-6">
            <div className="flex items-start gap-4">
              {driverStatus === 'pending_approval' ? (
                <Clock className="w-8 h-8 text-orange-600 flex-shrink-0" strokeWidth={2} />
              ) : (
                <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0" strokeWidth={2} />
              )}
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{statusLabels[driverStatus] || 'Unknown Status'}</h2>
                {driverStatus === 'pending_approval' ? (
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your application has been submitted and is awaiting approval from your company admin.</p>
                ) : (
                  <>
                    <p className="text-sm text-slate-700 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Please complete your employment application and upload required documents.</p>
                    <button
                      data-testid="complete-application-button"
                      onClick={() => navigate('/onboarding/application')}
                      className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Complete Application
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const IN_PROGRESS = DRIVER_IN_PROGRESS;
  const currentLoad = loads.find((l) => !l.is_co_driver && IN_PROGRESS.includes(l.status)) || null;
  const assignedLoads = loads
    .filter((l) => !l.is_co_driver && l.status === 'assigned')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const completedLoads = loads
    .filter((l) => !l.is_co_driver && l.status === 'delivered')
    .sort((a, b) => new Date(b.delivered_at || b.created_at) - new Date(a.delivered_at || a.created_at));
  // Team loads: this driver rode as co-driver. Read-only — the primary drives + controls status.
  const teamLoads = loads
    .filter((l) => l.is_co_driver)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const teamStatusLabel = statusLabel;

  return (
    <DashboardLayout>
      <OfflineBanner onSynced={fetchAll} />
      <TrackingSessionBanner />
      <DriverRequestModal
        open={requestOpen}
        loadId={requestLoadId}
        onClose={() => { setRequestOpen(false); setRequestLoadId(null); }}
      />
      {/* Floating "New request" FAB — always accessible from driver dashboard.
          Sits above the mobile bottom-nav (z-50 > nav z-40 and clear of the
          56px nav bar on small viewports). */}
      <button
        type="button"
        data-testid="driver-request-fab"
        onClick={() => { setRequestLoadId(null); setRequestOpen(true); }}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-full shadow-lg px-4 py-3 text-sm font-semibold flex items-center gap-2"
      >
        <span aria-hidden="true">+</span> Request
      </button>
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Welcome back, {user.name}</h1>
          <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Here's an overview of your loads.</p>
        </div>

        <ContractExpiryPanel />

        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div data-testid="stat-current" className="bg-white border border-slate-200 rounded-md p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Current</p>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="stat-current-status">{currentLoad ? statusLabel(currentLoad.status) : '—'}</p>
          </div>
          <div data-testid="stat-assigned" className="bg-white border border-slate-200 rounded-md p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Queued</p>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{assignedLoads.length}</p>
          </div>
          <div data-testid="stat-completed" className="bg-white border border-slate-200 rounded-md p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Completed</p>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{completedLoads.length}</p>
          </div>
        </div>

        {/* Submit Receipt (mobile-first) */}
        <section className="mb-8 pb-4">
          <ShareLocationPrompt load={currentLoad} />
          {currentLoad && (
            <div className="mb-3 bg-white border border-slate-200 rounded-xl p-4" data-testid="trip-stop-bar">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Log a stop on this trip</p>
              <div className="flex flex-wrap gap-2">
                {[['fuel', 'Fuel', Fuel], ['scale', 'Scale', Scale], ['repair', 'Repair', Wrench]].map(([type, label, Icon]) => (
                  <label key={type} data-testid={`stop-btn-${type}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7] transition-all cursor-pointer ${stopBusy === type ? 'opacity-50 pointer-events-none' : ''}`}>
                    {stopBusy === type ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
                    <input type="file" accept="image/*,.pdf" capture="environment" className="hidden"
                      onChange={(e) => stopWithPhoto(currentLoad.id || currentLoad._id, type, e.target.files?.[0])} />
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tap a stop to snap its receipt — the stop and receipt are saved together. No receipt handy? Use "Take / Choose Photo" below.</p>
            </div>
          )}
          <DriverReceiptCard activeLoadId={currentLoad?.id || currentLoad?._id || null} stopPrefill={stopPrefill} refreshKey={receiptRefresh} />
          {currentLoad && (currentLoad.trip_stops || []).length > 0 && (
            <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4" data-testid="driver-trip-stops">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trip stops logged</p>
              <LoadStopsMap stops={currentLoad.trip_stops} height={200} />
            </div>
          )}
        </section>

        {/* CURRENT ACTIVE LOAD */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 bg-[#34C759] rounded-sm"></div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Current Active Load</h2>
          </div>

          {currentLoad ? (
            <div data-testid="current-load-card" className="bg-white border-2 border-[#34C759] rounded-md p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center">
                    <Truck className="w-6 h-6 text-[#34C759]" strokeWidth={2} />
                  </div>
                  <div>
                    <LoadStatusPill status={currentLoad.status} size="xs" testId="current-load-status-pill" />
                    <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{currentLoad.cargo_description || 'Active shipment'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#34C759] mt-1" strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>From</p>
                    <p data-testid="current-pickup" className="text-base font-medium text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{currentLoad.pickup_location}</p>
                    {currentLoad.pickup_time && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}><Calendar className="w-3 h-3" />{currentLoad.pickup_time}</p>}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FF3B30] mt-1" strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>To</p>
                    <p data-testid="current-delivery" className="text-base font-medium text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{currentLoad.delivery_location}</p>
                    {currentLoad.delivery_time && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}><Calendar className="w-3 h-3" />{currentLoad.delivery_time}</p>}
                  </div>
                </div>
              </div>

              {(currentLoad.weight || currentLoad.distance) && (
                <div className="flex gap-6 pt-4 border-t border-slate-200 mb-6 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {currentLoad.weight && (
                    <div className="flex items-center gap-2"><Weight className="w-4 h-4 text-slate-500" /><span className="text-slate-700">{currentLoad.weight}</span></div>
                  )}
                  {currentLoad.distance && (
                    <div className="flex items-center gap-2"><RouteIcon className="w-4 h-4 text-slate-500" /><span className="text-slate-700">{currentLoad.distance}</span></div>
                  )}
                </div>
              )}

              {/* Operational details from rate confirmation (PDF-extracted loads) */}
              {(currentLoad.equipment_type || currentLoad.reefer_temp_f != null || currentLoad.commodity || currentLoad.broker_name) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="current-load-operational">
                  {currentLoad.equipment_type && (
                    <div className="bg-slate-50 rounded-md p-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Equipment</p>
                      <p className="text-sm font-semibold text-slate-800 capitalize">{currentLoad.equipment_type.replace('_', ' ')}{currentLoad.equipment_length_ft ? ` · ${currentLoad.equipment_length_ft}'` : ''}</p>
                    </div>
                  )}
                  {currentLoad.reefer_temp_f != null && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2" data-testid="current-reefer-badge">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-700">Reefer</p>
                      <p className="text-sm font-bold text-blue-900">{currentLoad.reefer_temp_f}°F</p>
                      {currentLoad.reefer_mode && <p className="text-[10px] text-blue-700 capitalize">{currentLoad.reefer_mode.replace('_', ' ')}</p>}
                    </div>
                  )}
                  {currentLoad.commodity && (
                    <div className="bg-slate-50 rounded-md p-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Commodity</p>
                      <p className="text-sm font-semibold text-slate-800 truncate" title={currentLoad.commodity}>{currentLoad.commodity}</p>
                      {currentLoad.freight_class && <p className="text-[10px] text-slate-500">Class {currentLoad.freight_class}</p>}
                    </div>
                  )}
                  {currentLoad.broker_name && (
                    <div className="bg-slate-50 rounded-md p-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Broker</p>
                      <p className="text-sm font-semibold text-slate-800 truncate" title={currentLoad.broker_name}>{currentLoad.broker_name}</p>
                      {currentLoad.broker_phone && <p className="text-[10px] text-slate-500">{currentLoad.broker_phone}</p>}
                    </div>
                  )}
                </div>
              )}

              {currentLoad.is_hazmat && (
                <div className="bg-red-50 border border-red-300 rounded-md p-3 mb-4 flex items-start gap-2" data-testid="current-hazmat-warning">
                  <span className="text-xl leading-none">⚠️</span>
                  <div className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <p className="font-bold text-red-900">HAZMAT LOAD</p>
                    {currentLoad.hazmat_class && <p className="text-red-800">Class: {currentLoad.hazmat_class}{currentLoad.un_number ? ` · UN${currentLoad.un_number}` : ''}</p>}
                  </div>
                </div>
              )}

              {currentLoad.special_instructions && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-4" data-testid="current-special-instructions">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Special Instructions</p>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{currentLoad.special_instructions}</p>
                </div>
              )}

              {/* Operational requirements row */}
              {(currentLoad.tracking_platform || currentLoad.lumper_handling || currentLoad.min_drive_miles_after_pickup || currentLoad.team_driver_required) && (
                <div className="flex flex-wrap gap-2 mb-4 text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="current-load-requirements">
                  {currentLoad.tracking_platform && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-100 text-purple-800 font-semibold" title="Tracking platform">
                      📡 {currentLoad.tracking_platform}
                    </span>
                  )}
                  {currentLoad.team_driver_required && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">
                      👥 Team Driver
                    </span>
                  )}
                  {currentLoad.exclusive_use_required && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-200 text-slate-800 font-semibold">
                      🔒 Exclusive Use
                    </span>
                  )}
                  {currentLoad.min_drive_miles_after_pickup && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Min drive after pickup (security)">
                      🛣 Drive {currentLoad.min_drive_miles_after_pickup}+ mi after pickup
                    </span>
                  )}
                  {currentLoad.lumper_handling && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-700">
                      🏗 Lumper: {currentLoad.lumper_handling}
                    </span>
                  )}
                  {currentLoad.reefer_precool_required && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-900 font-semibold">
                      ❄️ Precool required
                    </span>
                  )}
                </div>
              )}

              {/* Multi-stop itinerary */}
              {Array.isArray(currentLoad.stops) && currentLoad.stops.length > 2 && (
                <div className="mb-6" data-testid="current-load-stops">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Itinerary ({currentLoad.stops.length} stops)</p>
                  <ol className="space-y-2">
                    {currentLoad.stops.map((s, i) => {
                      const typeBg = {
                        pickup: 'border-blue-200 bg-blue-50',
                        delivery: 'border-green-200 bg-green-50',
                        cross_dock: 'border-purple-200 bg-purple-50',
                        layover: 'border-amber-200 bg-amber-50',
                        relay: 'border-slate-200 bg-slate-50',
                      }[s.stop_type] || 'border-slate-200 bg-slate-50';
                      const ref = s.pickup_number || s.delivery_number || s.po_number || s.confirmation_number;
                      const apptLabel = ({appt:'APPT', fcfs:'FCFS', window:'WINDOW', first_come:'FIRST COME'})[s.appointment_type];
                      const apptText = s.appointment_time || (s.appointment_window_start && s.appointment_window_end
                          ? `${s.appointment_window_start} → ${s.appointment_window_end}` : (s.appointment_window_start || s.appointment_window_end));
                      return (
                        <li key={i} className={`border rounded-md p-2.5 text-xs ${typeBg}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-slate-800">
                                #{s.sequence || i + 1} <span className="capitalize text-slate-600">{(s.stop_type || '').replace('_', ' ')}</span>
                                {apptLabel && <span className="ml-2 px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[10px] font-bold text-slate-700">{apptLabel}</span>}
                                — {s.facility_name || `${s.city || ''}${s.city && s.state ? ', ' : ''}${s.state || ''}`}
                                {s.store_number && <span className="ml-1 text-slate-500">({s.store_number})</span>}
                              </p>
                              {(s.address || s.city) && <p className="text-slate-600">{[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')}</p>}
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-700">
                                {apptText && <span>📅 {apptText}</span>}
                                {s.appointment_number && <span>🎫 Appt# {s.appointment_number}</span>}
                                {ref && <span>🏷 {ref}</span>}
                                {s.weight_lbs && <span>⚖️ {Number(s.weight_lbs).toLocaleString()} lbs</span>}
                                {s.pallet_count != null && <span>🪵 {s.pallet_count} plt</span>}
                                {s.case_count != null && <span>📦 {Number(s.case_count).toLocaleString()} cs</span>}
                                {s.cubic_feet != null && <span>📏 {Number(s.cubic_feet).toLocaleString()} cu ft</span>}
                                {s.contact_phone && <a href={`tel:${s.contact_phone}`} className="text-[#002FA7] font-semibold">📞 {s.contact_phone}</a>}
                              </div>
                              {Array.isArray(s.references) && s.references.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {s.references.map((r, ri) => (
                                    <span key={ri} className="px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[10px]">
                                      <span className="uppercase text-slate-500 mr-0.5">{r.kind}</span>
                                      <span className="font-mono">{r.number}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {Array.isArray(s.items) && s.items.length > 0 && (
                                <details className="mt-1.5">
                                  <summary className="cursor-pointer text-[11px] text-slate-600 hover:text-slate-900">{s.items.length} line item{s.items.length > 1 ? 's' : ''}</summary>
                                  <table className="w-full mt-1 text-[11px]">
                                    <thead><tr className="text-left text-slate-500"><th>PO</th><th>Qty</th><th>Wt</th><th>Cu ft</th></tr></thead>
                                    <tbody>
                                      {s.items.map((it, ii) => (
                                        <tr key={ii} className="border-t border-slate-200/60">
                                          <td className="font-mono">{it.po_number || it.sku || '—'}</td>
                                          <td>{it.quantity ? `${Number(it.quantity).toLocaleString()}${it.unit ? ' ' + it.unit : ''}` : '—'}</td>
                                          <td>{it.weight_lbs ? `${Number(it.weight_lbs).toLocaleString()} lb` : '—'}</td>
                                          <td>{it.cubic_feet ? `${Number(it.cubic_feet).toLocaleString()}` : '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </details>
                              )}
                              {s.notes && <p className="mt-1 text-slate-600 italic">{s.notes}</p>}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {currentLoad.notes && (
                <div className="p-3 bg-slate-50 rounded-md mb-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</p>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{currentLoad.notes}</p>
                </div>
              )}

              <div className="mb-6">
                <LoadStatusStepper
                  load={currentLoad}
                  busy={updating === currentLoad.id}
                  onAdvance={(st) => handleUpdateStatus(currentLoad.id, st)}
                  onComplete={() => { track('click', 'complete-trip', { loadId: currentLoad?.id }); setPostTripLoad(currentLoad); }}
                />
                <StopProgressCard loadId={currentLoad.id} />
              </div>

              {/* Compliance — pending POD/BOL/Rate Con (driver-facing nudge) */}
              {Array.isArray(currentLoad.missing_doc_kinds) && currentLoad.missing_doc_kinds.length > 0 && (
                <div
                  data-testid="driver-compliance-pending"
                  className="mb-6 p-4 rounded-md border border-orange-200 bg-orange-50/60 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p
                      className="text-xs font-semibold uppercase tracking-widest text-orange-700 mb-1"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Pending Compliance
                    </p>
                    <p
                      className="text-sm font-medium text-slate-800"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Upload missing documents before ending your trip.
                    </p>
                    <div className="mt-2">
                      <MissingDocsBadge missingKinds={currentLoad.missing_doc_kinds} />
                    </div>
                  </div>
                  <button
                    data-testid="driver-compliance-upload"
                    onClick={() => setDocsLoad(currentLoad)}
                    className="flex-shrink-0 px-3 py-2 rounded-md text-xs font-semibold text-white bg-[#002FA7] hover:bg-[#00247D]"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    Upload now
                  </button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    data-testid="current-load-chat"
                    onClick={() => setChatLoad({ id: currentLoad.id, load_ref: currentLoad.load_ref || currentLoad.id.slice(-6) })}
                    className="relative flex-1 sm:flex-none bg-white border border-slate-300 text-slate-700 py-3 sm:py-4 px-4 sm:px-5 rounded-md font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-all flex items-center justify-center gap-2 active:scale-95"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    title="Open chat with dispatcher"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Chat
                    {currentLoad.unread_messages > 0 && (
                      <span data-testid={`current-load-unread-${currentLoad.id}`} className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold ring-2 ring-white">
                        {currentLoad.unread_messages > 9 ? '9+' : currentLoad.unread_messages}
                      </span>
                    )}
                  </button>
                  <button
                    data-testid="current-load-docs"
                    onClick={() => setDocsLoad(currentLoad)}
                    className="flex-1 sm:flex-none bg-white border border-slate-300 text-slate-700 py-3 sm:py-4 px-4 sm:px-5 rounded-md font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-all flex items-center justify-center gap-2 active:scale-95"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    title="Upload POD/BOL/Lumper/Scale Ticket"
                  >
                    <FileBadge2 className="w-5 h-5" />
                    Docs
                  </button>
                </div>
                <button
                  data-testid="mark-delivered-button"
                  onClick={() => { track('click', 'complete-trip', { loadId: currentLoad?.id }); setPostTripLoad(currentLoad); }}
                  disabled={updating === currentLoad.id}
                  className="flex-1 bg-[#002FA7] text-white py-4 px-6 rounded-md text-base sm:text-lg font-bold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <CheckCircle className="w-5 h-5" />
                  Complete Trip
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="no-current-load" className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No load in transit right now.</p>
              {assignedLoads.length > 0 && (
                <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Start one of your assigned loads below.</p>
              )}
            </div>
          )}
        </section>

        {/* ASSIGNED LOADS */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 bg-[#002FA7] rounded-sm"></div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Assigned Loads</h2>
            <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>({assignedLoads.length})</span>
          </div>

          {assignedLoads.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-6 text-center">
              <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No assigned loads in your queue.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assignedLoads.map((load) => (
                <div key={load.id} data-testid={`assigned-load-${load.id}`} className="bg-white border border-slate-200 rounded-md p-5 hover:border-[#002FA7] transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">Assigned</span>
                        {load.cargo_description && (
                          <span className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{load.cargo_description}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-900 font-medium mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="truncate">{load.pickup_location}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{load.delivery_location}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {load.pickup_time && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{load.pickup_time}</span>}
                        {load.weight && <span className="flex items-center gap-1"><Weight className="w-3 h-3" />{load.weight}</span>}
                        {load.distance && <span className="flex items-center gap-1"><RouteIcon className="w-3 h-3" />{load.distance}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                    <button
                      data-testid={`assigned-load-chat-${load.id}`}
                      onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                      className="relative inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-300 text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex-shrink-0"
                      title="Open chat with dispatcher"
                    >
                      <MessageSquare className="w-5 h-5" />
                      {load.unread_messages > 0 && (
                        <span data-testid={`assigned-load-unread-${load.id}`} className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white">
                          {load.unread_messages > 9 ? '9+' : load.unread_messages}
                        </span>
                      )}
                    </button>
                    <button
                      data-testid={`start-trip-${load.id}`}
                      onClick={() => setPreTripLoad(load)}
                      disabled={!!currentLoad || updating === load.id}
                      title={currentLoad ? 'Finish your current load first' : 'Start this trip'}
                      className="bg-[#34C759] text-white px-5 py-2.5 rounded-md font-semibold hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <Truck className="w-4 h-4" />
                      Start Trip
                    </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* TEAM LOADS (co-driver — read-only) */}
        {teamLoads.length > 0 && (
          <section data-testid="team-loads-section">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-6 bg-[#002FA7] rounded-sm"></div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Team Loads</h2>
              <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>({teamLoads.length})</span>
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-[#002FA7]/10 text-[#002FA7]">
                <Users className="w-3 h-3" /> Co-driver
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3 -mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Runs where you're the second driver. These are read-only — the primary driver controls the trip. Your share is paid via a Pay Transfer on payroll.
            </p>
            <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
              {teamLoads.map((load) => (
                <div key={load.id} data-testid={`team-load-${load.id}`} className="p-4 md:p-5 flex items-center justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Users className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="truncate">{load.pickup_location}</span>
                        <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{load.delivery_location}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {load.load_ref && <span>{load.load_ref} • </span>}
                        {load.status === 'delivered'
                          ? `Delivered ${load.delivered_at ? new Date(load.delivered_at).toLocaleDateString() : '-'}`
                          : teamStatusLabel(load.status)}
                      </p>
                    </div>
                  </div>
                  <div className="ml-3 flex items-center gap-2 flex-shrink-0">
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {teamStatusLabel(load.status)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                      data-testid={`team-load-chat-${load.id}`}
                      className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-[#002FA7] transition-colors"
                      aria-label="Open load chat"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {load.unread_messages > 0 && (
                        <span
                          data-testid={`team-load-unread-${load.id}`}
                          className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white"
                        >
                          {load.unread_messages > 9 ? '9+' : load.unread_messages}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* COMPLETED LOADS */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 bg-slate-400 rounded-sm"></div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Completed Loads</h2>
            <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>({completedLoads.length})</span>
          </div>

          {completedLoads.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-6 text-center">
              <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No completed loads yet. Keep driving!</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
              {completedLoads.map((load) => (
                <div key={load.id} data-testid={`completed-load-${load.id}`} className="p-4 md:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="truncate">{load.pickup_location}</span>
                        <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{load.delivery_location}</span>
                        <MissingDocsBadge missingKinds={load.missing_doc_kinds} className="ml-2 flex-shrink-0" />
                      </div>
                      <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {load.cargo_description && <span>{load.cargo_description} • </span>}
                        Delivered {load.delivered_at ? new Date(load.delivered_at).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      data-testid={`completed-load-docs-${load.id}`}
                      onClick={() => setDocsLoad(load)}
                      title="View / upload documents"
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold text-[#002FA7] border border-slate-200 hover:border-[#002FA7] flex items-center gap-1.5"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <FileBadge2 className="w-3.5 h-3.5" />
                      Docs
                    </button>
                    {load.distance && (
                      <span className="text-xs text-slate-500 whitespace-nowrap" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{load.distance}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      {chatLoad && (
        <LoadChatDrawer
          loadId={chatLoad?.id}
          loadRef={chatLoad?.load_ref}
          open={!!chatLoad}
          onClose={() => { setChatLoad(null); fetchAll(); }}
        />
      )}
      <LoadDocumentsModal
        load={docsLoad}
        role={user?.role}
        currentUserId={user?.id}
        open={!!docsLoad}
        onClose={() => setDocsLoad(null)}
      />
      {preTripLoad && (
        <PreTripModal
          load={preTripLoad}
          onClose={() => setPreTripLoad(null)}
          onComplete={async () => { setPreTripLoad(null); await fetchAll(); }}
        />
      )}
      {postTripLoad && (
        <PostTripModal
          load={postTripLoad}
          onClose={() => setPostTripLoad(null)}
          onComplete={async () => { setPostTripLoad(null); await fetchAll(); }}
        />
      )}
    </DashboardLayout>
  );
};

export default DriverDashboard;